<template>
  <HyperCodeHeroProductSection
    :id="item.fields.id"
    ref="heroRoot"
    :title="headline"
    :second-title="secondTitle"
    :text="text"
    :buttons="buttons"
    :media="media"
    :lazyload="false"
    :text-side="textSide"
    :color-mode="lightDarkColorMode"
    v-bind="
      $ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'headline'
      })
    "
  >
    <template #rteText>
      <LibRichTextRenderer v-if="descriptionRte" :rich-text="descriptionRte" />
    </template>
  </HyperCodeHeroProductSection>
</template>

<script setup lang="ts">
import { HeroProductSection as HyperCodeHeroProductSection } from '@hypercodestudio/basler-components';
import type { HeroProductSectionTextSide as TextSide } from '@hypercodestudio/basler-components';

import { getLinkIconPosition } from '~/utils/getLinkIconPosition';
import type { IHeroProductSection } from '~~/lib/ContentfulService';
import { useMedia } from '~/composables/useMedia';
import { useProgressiveHeroMedia } from '~/composables/useProgressiveHeroMedia';
import { isEntryResolveError } from '~/utils/guards/isEntryResolveError';
import { isDefined } from '~/utils/guards/isDefined';
import type { IMedia } from '~/utils/guards/isMediaType';
import { mapHeroSubtitle } from '~/utils/mapHeroSubtitle';
import { mapHeroHeadline } from '~/utils/mapHeroHeadline';
import { heroImageSizes, heroImageWidths } from '~/components/global/constants';
import {
  toDeprecatedColorMode,
  useInjectedColorMode
} from '~/composables/useInjectedColorMode';

interface Props {
  item: IHeroProductSection;
}
const props = defineProps<Props>();
const { $ContentfulLivePreview } = useNuxtApp();

const heroRoot = ref<InstanceType<typeof HyperCodeHeroProductSection>>();

const headline = computed(() => mapHeroHeadline(props.item));
const secondTitle = computed(() => mapHeroSubtitle(props.item));

// Der Link Type beinhaltet aktuell nur einen Text. Hypercode wurde in Ticket #1260 darüber informiert.
const buttons = computed(() =>
  props.item.fields.links
    ?.filter(isDefined)
    ?.filter((entry) => !isEntryResolveError(entry))
    ?.map((field) => ({
      text: field.fields.linkText ?? '',
      link: useBuildLinkInterface(field),
      size: field.fields.linkStyleSize,
      styleType: field.fields.linkStyle ?? 'default',
      iconName: field.fields.linkIcon,
      targetBlank: field.fields.linkTarget === '_blank',
      iconType: getLinkIconPosition(field.fields.linkIconPosition)
    }))
);

const text = computed(() => {
  if (props.item.fields.text) {
    return {
      lead: false,
      content: props.item.fields.text
    };
  }
  return undefined;
});

const descriptionRte = computed(() => props.item.fields.descriptionRte ?? '');

const lightDarkColorMode = toDeprecatedColorMode(useInjectedColorMode());

const textSide = computed<TextSide>(
  () => props.item.fields.textAlignment ?? 'left'
);

const baseMedia = await useMedia(props.item.fields.media as IMedia, {
  keepAspectRatio: true,
  sizes: heroImageSizes,
  widths: heroImageWidths
});

const { media } = useProgressiveHeroMedia(baseMedia, heroRoot);
</script>
